import { Button, TextField, Autocomplete, Stack, Tooltip } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import IconButton from '@mui/material/IconButton';
import { CUSTOM_RULE_OPERATOR_VALUE } from './CustomRuleDetail';

const autocompleteStyles = {
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};

const textInputStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const andButtonStyle = {
  fontSize: 12,
  lineHeight: 1.3,
  px: 1.5,
  py: 0.5,
  minWidth: 0,
  borderColor: '#A5D6A7',
  color: '#2E7D32',
  bgcolor: '#E8F5E9',
  fontWeight: 700,
  '&:hover': {
    bgcolor: '#C8E6C9',
    borderColor: '#66BB6A',
  },
  '&.Mui-disabled': {
    borderColor: '#E0E0E0',
    color: '#BDBDBD',
    bgcolor: '#FAFAFA',
  },
};

const orButtonStyle = {
  fontSize: 12,
  lineHeight: 1.3,
  px: 1.5,
  py: 0.5,
  minWidth: 0,
  borderColor: '#FFCC80',
  color: '#E65100',
  bgcolor: '#FFF3E0',
  fontWeight: 700,
  '&:hover': {
    bgcolor: '#FFE0B2',
    borderColor: '#FFA726',
  },
  '&.Mui-disabled': {
    borderColor: '#E0E0E0',
    color: '#BDBDBD',
    bgcolor: '#FAFAFA',
  },
};

const removeIconButtonStyle = {
  color: '#BB4942',
  border: '1px solid #FFCDD2',
  bgcolor: '#FFF5F5',
  borderRadius: 1,
  width: 32,
  height: 32,
  '&:hover': {
    bgcolor: '#FFEBEE',
    borderColor: '#EF9A9A',
  },
};

function getDisabledNextButton(formik, index) {
  const operatorValue = formik.values?.conditions?.[index]?.operator;

  if (operatorValue === CUSTOM_RULE_OPERATOR_VALUE.NOT_EMPTY) {
    return !formik.values?.conditions?.[index]?.field;
  }

  const disabledNextButton =
    !formik.values?.conditions?.[index]?.field || !operatorValue || !formik.values?.conditions?.[index]?.value;
  return disabledNextButton;
}

export const ConditionField = ({ condition, index, isLast, fieldData, formik, onRemove, onAdd }) => {
  const fieldName = `conditions[${index}].field`;
  const operatorName = `conditions[${index}].operator`;
  const valueName = `conditions[${index}].value`;
  const field = fieldData.find((item) => item.fieldName === condition.field) || {};
  const operatorValue = formik.values?.conditions?.[index]?.operator;
  const disabledOperator = !formik.values?.conditions?.[index]?.field;
  const disabledValue = !formik.values?.conditions?.[index]?.field || !operatorValue;
  const disabledNextButton = getDisabledNextButton(formik, index);

  let InputElement;
  if (field.type === 'OPTIONS') {
    InputElement = (
      <Autocomplete
        options={field.options}
        getOptionLabel={(option) => option.label}
        value={field.options.find((option) => option.value === condition.value) || null}
        onChange={(_, newValue) => {
          formik.setFieldValue(valueName, newValue ? newValue.value : '');
        }}
        disabled={disabledValue}
        sx={autocompleteStyles}
        clearIcon={null}
        renderInput={(params) => <TextField {...params} placeholder='Select Value...' />}
      />
    );
  } else if (field.type === 'URL') {
    InputElement = (
      <TextField
        type='url'
        sx={textInputStyles}
        name={valueName}
        value={condition.value}
        onChange={formik.handleChange}
        hiddenLabel
        disabled={disabledValue}
        variant='outlined'
        size='small'
      />
    );
  } else if (field.type === 'NUMBER') {
    InputElement = (
      <TextField
        sx={textInputStyles}
        type='number'
        name={valueName}
        disabled={disabledValue}
        hiddenLabel
        variant='outlined'
        size='small'
        value={condition.value}
        onChange={formik.handleChange}
      />
    );
  } else {
    InputElement = (
      <TextField
        sx={textInputStyles}
        name={valueName}
        value={condition.value}
        onChange={formik.handleChange}
        hiddenLabel
        disabled={disabledValue}
        variant='outlined'
        size='small'
      />
    );
  }

  if (operatorValue === CUSTOM_RULE_OPERATOR_VALUE.NOT_EMPTY) {
    InputElement = null;
  }

  let OperatorElement = (
    <Autocomplete
      options={field.operator || []}
      getOptionLabel={(option) => option}
      value={condition.operator || null}
      disabled={disabledOperator}
      onChange={(_, newValue) => {
        formik.setFieldValue(operatorName, newValue ? newValue : '');
      }}
      clearIcon={null}
      sx={{ ...autocompleteStyles, width: '100%' }}
      renderInput={(params) => <TextField {...params} placeholder='Select Operator...' />}
    />
  );

  let FieldElement = (
    <Autocomplete
      options={fieldData}
      getOptionLabel={(option) => option.fieldName}
      value={fieldData.find((item) => item.fieldName === condition.field) || null}
      onChange={(_, newValue) => {
        formik.setFieldValue(fieldName, newValue ? newValue.fieldName : '');
      }}
      clearIcon={null}
      sx={autocompleteStyles}
      renderInput={(params) => <TextField {...params} placeholder='Select Attribute...' />}
    />
  );

  const showRemove = (!isLast || index !== 0);

  return (
    <Stack direction='row' gap={1.5} width='100%' alignItems='center'>
      <Stack width='100%'>{FieldElement}</Stack>
      <Stack width='75%'>{OperatorElement}</Stack>
      <Stack width='100%'>{InputElement}</Stack>

      <Stack direction='row' gap={0.75} alignItems='center' sx={{ minWidth: 'fit-content' }}>
        {isLast && (
          <>
            <Tooltip title='Add AND condition' arrow placement='top'>
              <span>
                <Button
                  variant='outlined'
                  size='small'
                  disabled={disabledNextButton}
                  sx={andButtonStyle}
                  onClick={() => onAdd('AND')}
                  startIcon={<AddRoundedIcon sx={{ fontSize: '16px !important' }} />}
                >
                  AND
                </Button>
              </span>
            </Tooltip>
            <Tooltip title='Add OR condition' arrow placement='top'>
              <span>
                <Button
                  variant='outlined'
                  size='small'
                  disabled={disabledNextButton}
                  sx={orButtonStyle}
                  onClick={() => onAdd('OR')}
                  startIcon={<AddRoundedIcon sx={{ fontSize: '16px !important' }} />}
                >
                  OR
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        {showRemove && (
          <Tooltip title='Remove condition' arrow placement='top'>
            <IconButton size='small' onClick={() => onRemove(index)} sx={removeIconButtonStyle}>
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};
